import { useEffect, useState } from 'react'
import { ErrorConstant, InvoiceService } from 'utility'

export const GetCompletedAudits = () => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [outputFile, setOutputFile] = useState('')
    const [pagination, setPagination] = useState({
        currentPage: 1,
        lastPage: 1,
        perPage: 20,
        total: 0,
        from: 1,
        to: 20,
    })
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: 50,
    })

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await InvoiceService.getCompletedAudits(filter)
                setData(result.Items)
                setOutputFile(result.OutputFile)
                if (result.MetaData) {
                    setPagination({
                        currentPage: result.MetaData.CurrentPage,
                        lastPage: result.MetaData.LastPage,
                        perPage: result.MetaData.PerPage,
                        total: result.MetaData.Total,
                        from: result.MetaData.From,
                        to: result.MetaData.To,
                    })
                }
                setTotalCount(result.TotalCount)
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }
        if (filter.StartDate && filter.EndDate) {
            fetchData()
        }
    }, [filter])

    const filterChanged = filterData => {
        setFilter({
            ...filter,
            ...filterData,
        })
    }

    const pageChanged = page => {
        setFilter({
            ...filter,
            PageNumber: page,
        })
    }

    const refresh = () => {
        setFilter({
            ...filter,
        })
    }

    return {
        data,
        error,
        setData,
        loading,
        filter,
        totalCount,
        outputFile,
        pagination,
        filterChanged,
        pageChanged,
        refresh,
    }
}

// Fetch single invoice by id (header + lines + payments)
export const GetInvoiceById = invoiceId => {
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    const fetch = async id => {
        if (!id) return
        try {
            setLoading(true)
            const res = await InvoiceService.getById(id)
            setData(res)
        } catch (e) {
            setError(ErrorConstant?.default || 'Failed to load invoice')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetch(invoiceId)
    }, [invoiceId])

    const refetch = () => fetch(invoiceId)

    return { data, setData, loading, error, refetch }
}

export const GetInvoiceSearch = () => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [outputFile, setOutputFile] = useState('')
    const [pagination, setPagination] = useState({
        currentPage: 1,
        lastPage: 1,
        perPage: 50,
        total: 0,
        from: 1,
        to: 50,
    })
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: 50,
    })

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await InvoiceService.searchInvoices(filter)
                setData(result.Items || [])
                setOutputFile(result.OutputFile || '')
                const currentPage = result.PageNumber || 1
                const perPage = result.PageSize || 50
                const total =
                    result.TotalCount ||
                    (result.Items ? result.Items.length : 0)
                const lastPage = Math.max(
                    1,
                    Math.ceil((total || 0) / (perPage || 1)),
                )
                const from = total ? (currentPage - 1) * perPage + 1 : 0
                const to = total ? Math.min(currentPage * perPage, total) : 0
                setPagination({
                    currentPage,
                    lastPage,
                    perPage,
                    total,
                    from,
                    to,
                })
                setTotalCount(total)
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }
        if (filter.FromInvoiceDate && filter.ToInvoiceDate) {
            fetchData()
        }
    }, [filter])

    const filterChanged = filterData => {
        setFilter({
            ...filter,
            ...filterData,
        })
    }

    const pageChanged = page => {
        setFilter({
            ...filter,
            PageNumber: page,
        })
    }

    const refresh = () => {
        setFilter({
            ...filter,
        })
    }

    return {
        data,
        error,
        setData,
        loading,
        filter,
        totalCount,
        outputFile,
        pagination,
        filterChanged,
        pageChanged,
        refresh,
    }
}

export const useCurrenciesOptions = () => {
    const [options, setOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        const fetchCurrencies = async () => {
            try {
                setLoading(true)
                const res = await InvoiceService.getCurrencies()
                const dataList = res?.data || res
                if (Array.isArray(dataList)) {
                    const mappedOptions = dataList.map(c => {
                        const code = c?.Code || (typeof c === 'string' ? c : '')
                        const name = c?.Name || ''
                        const text = name ? `${code} - ${name}` : code
                        return {
                            key: code,
                            text,
                            value: code,
                        }
                    })
                    setOptions(mappedOptions)
                }
            } catch (err) {
                console.error('Error fetching currencies options:', err)
                setError(ErrorConstant?.default || 'Failed to load currencies')
            } finally {
                setLoading(false)
            }
        }
        fetchCurrencies()
    }, [])

    return { data: options, loading, error }
}